import React from 'react'
import classNames from 'classnames'
import { CircularProgress, Tooltip } from '@mui/material'

import { TABLE_HEADERS } from '../../shared/types'
import { ISafetyTeamRecord } from '../../shared/types'
import { ReactComponent as DeleteIcon } from '../../../../assets/svg/delete.svg'
import { ReactComponent as ReopenIcon } from '../../../../assets/svg/restart_alt_12w.svg'

import styles from '../../SafetyTeam.module.scss'

interface IArchivedSafetyTeamTableProps {
  records: ISafetyTeamRecord[]
  onReopen: (recordId: string) => void
  onDelete: (recordId: string) => void
  isTableHidden: boolean
  isLoading: boolean
}

const ArchivedSafetyTeamTable = ({
  records,
  onReopen,
  onDelete,
  isTableHidden,
  isLoading,
}: IArchivedSafetyTeamTableProps) => {
  const renderRow = (record: ISafetyTeamRecord) => {
    const driverName = record.driver?.displayName || record.driver?.name || ''
    const documentsLabel = (record.documents || []).map((doc) => doc.name).join(', ')

    return (
      <tr key={record.id}>
        <td className={styles.readonlyField}>
          {driverName}
        </td>

        <td>
          <Tooltip title={record.driversLicenseNumber || ''} placement="top">
            <span className={styles.readonlyText}>
              {record.driversLicenseNumber || ' '}
            </span>
          </Tooltip>
        </td>

        <td>
          <span className={styles.readonlyText}>
            {record.driverType || ' '}
          </span>
        </td>

        <td>
          <span className={styles.readonlyText}>
            {record.testDate || ' '}
          </span>
        </td>

        <td>
          <span className={styles.readonlyText}>
            {record.assignedDate || ' '}
          </span>
        </td>

        <td>
          <Tooltip
            title={
              <div style={{ maxWidth: 160, whiteSpace: 'normal' }}>
                {documentsLabel || 'No documents'}
              </div>
            }
            placement="top-start"
          >
            <span className={styles.readonlyText}>
              {record.documents && record.documents.length > 0
                ? `${record.documents.length} file(s)`
                : 'No documents'}
            </span>
          </Tooltip>
        </td>

        <td>
          <span className={styles.readonlyText}>
            {record.archivedDate || ' '}
          </span>
        </td>

        <td>
          <div className={styles.actionButtons}>
            <Tooltip title="Reopen" placement="top">
              <button
                type="button"
                className={styles.reopenButton}
                onClick={() => onReopen(record.id)}
              >
                <ReopenIcon />
              </button>
            </Tooltip>

            <Tooltip title="Delete" placement="top">
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => onDelete(record.id)}
              >
                <DeleteIcon />
              </button>
            </Tooltip>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <div
      className={classNames({
        [styles.hidden]: isTableHidden,
        [styles.archivedTable]: !isTableHidden,
      })}
    >
      <div className={styles.tableContainer}>
        <table>
          <thead>
            <tr>
              {TABLE_HEADERS.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {records.map(renderRow)}

            {records.length === 0 && (
              <tr>
                <td colSpan={TABLE_HEADERS.length} className={styles.noData}>
                  {isLoading ? (
                    <CircularProgress color="inherit" />
                  ) : (
                    'No closed safety team records.'
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ArchivedSafetyTeamTable
